import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useApi, PageHeader, Loading, KpiCard, Select } from "../components/common";
import { useDirectory } from "../components/LeadForm";
import { inr, pct, fdate, todayIST } from "../lib/format";

const WON = "Won / Project Confirmed";

export default function Pipeline() {
  const { user } = useAuth();
  const [who, setWho] = useState("");
  const sales = useDirectory("sales");
  const { data, loading, reload } = useApi(`/pipeline${who ? `?assigned_to=${who}` : ""}`);
  const [drag, setDrag] = useState(null);
  const t = todayIST();
  const drop = async (stage) => {
    if (!drag || drag.status === stage) return;
    if (stage === WON) { toast.info("Open the lead and use 'Confirm Deal' to mark it won"); return; }
    let note = "";
    if (stage === "Lost") { note = window.prompt("Reason for losing this lead?") || ""; if (!note) return; }
    try { await api.post(`/leads/${drag.id}/stage`, { stage, note, lost_reason: note }); toast.success(`${drag.client_name} → ${stage}`); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setDrag(null); }
  };
  return (
    <div>
      <PageHeader title="Sales Pipeline" subtitle="Drag cards between stages. Won requires explicit deal confirmation."
        actions={user.role === "owner" && <Select testid="pipeline-filter-salesperson" className="w-52" placeholder="All salespeople" options={sales.map((s) => ({ value: s.id, label: s.name }))} value={who} onChange={setWho} />} />
      {loading && !data ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
            <KpiCard testid="pipe-open-value" label="Open Pipeline" value={inr(data.stats.open_value)} tone="revenue" />
            <KpiCard testid="pipe-expected" label="Expected Revenue" value={inr(data.stats.expected_revenue)} tone="profit" hint="stage-weighted" />
            <KpiCard testid="pipe-won" label="Won Deals" value={`${data.stats.won} · ${inr(data.stats.won_value)}`} tone="cash" />
            <KpiCard testid="pipe-lost" label="Lost Deals" value={data.stats.lost} tone="expense" />
            <KpiCard testid="pipe-conversion" label="Conversion" value={pct(data.stats.conversion_pct)} hint={`win rate ${pct(data.stats.win_rate_pct)}`} tone="revenue" />
            <KpiCard testid="pipe-followups" label="Follow-ups Due" value={data.stats.followups_due} tone="pending" />
          </div>
          <div className="flex gap-3 overflow-x-auto pb-4" data-testid="pipeline-board">
            {data.stages.map((s) => (
              <div key={s.stage} className="kanban-col shrink-0 rounded-xl bg-muted/60 p-2.5" onDragOver={(e) => e.preventDefault()} onDrop={() => drop(s.stage)} data-testid={`pipeline-col-${s.stage.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
                <div className="flex items-center justify-between px-1 mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-600">{s.stage}</span>
                  <span className="text-[11px] font-semibold text-primary">{s.count} · {inr(s.value)}</span>
                </div>
                <div className="space-y-2 min-h-[60px]">
                  {s.leads.map((l) => (
                    <Link to={`/leads/${l.id}`} key={l.id} draggable onDragStart={() => setDrag(l)} data-testid={`pipeline-card-${l.id}`}
                      className="block bg-white rounded-lg border p-3 hover:border-primary hover:shadow-md transition-shadow cursor-grab">
                      <div className="text-sm font-semibold truncate">{l.client_name}</div>
                      <div className="text-xs text-muted-foreground truncate">{l.studio_name}</div>
                      <div className="flex items-center justify-between mt-2 text-xs">
                        <span className="font-num font-semibold">{inr(l.deal_value || l.est_value)}</span>
                        {l.next_followup && <span className={l.next_followup <= t ? "text-rose-600 font-semibold" : "text-muted-foreground"}>{fdate(l.next_followup)}</span>}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
